import type { DeletionImpact } from '@/generated/api/models/deletionImpact';
import type { DeletionBlockerSample } from '@/generated/api/models/deletionBlockerSample';
import axiosInstance from '../../../../shared/api/axiosInstance';
import type {
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  Contact,
  ContactCreate,
  ContactUpdate,
  DomainEnrichmentResult,
  CompanyLookupResult,
  ListOrganizationsParams,
  ListContactsParams,
  PaginatedResponse,
  Tier,
  TierCreate,
  TierUpdate,
  DuplicateMatch,
  DuplicateCheckPayload,
} from '../types';
import type { ContactListFilterInput } from '../../../../generated/api/models/contactListFilterInput';
import type { OrganizationListFilterInput } from '../../../../generated/api/models/organizationListFilterInput';
import type { BulkContactRequest } from '../../../../generated/api/models/bulkContactRequest';
import type { BulkOrganizationRequest } from '../../../../generated/api/models/bulkOrganizationRequest';
import {
  bulkArchiveContactsApiV1MasterDataContactsPeopleBulkArchivePost,
  bulkArchiveOrganizationsApiV1MasterDataContactsOrganizationsBulkArchivePost,
  bulkDeleteContactsApiV1MasterDataContactsPeopleBulkDeletePost,
  bulkDeleteOrganizationsApiV1MasterDataContactsOrganizationsBulkDeletePost,
  getContactDeletionImpactApiV1MasterDataContactsPeopleContactIdDeletionImpactGet,
  getOrganizationDeletionImpactApiV1MasterDataContactsOrganizationsOrganizationIdDeletionImpactGet,
} from '../../../../generated/api/contacts/contacts';

const BASE_URL = '/api/v1/master-data/contacts';

/**
 * The set a destructive bulk action applies to: the ids the browser loaded, or
 * every row matching the list's current filters.
 *
 * A union rather than two optional fields, so a call site cannot post both scopes
 * or neither — `EscalatableBulkRequest._exactly_one_scope` says the same thing at
 * runtime (422), and this moves the rejection to compile time.
 *
 * Parameterized by the filter shape rather than one type for BOTH entities: the
 * two routes take the same two scopes but each validates its OWN filter
 * vocabulary with `extra="forbid"`, so a union of the two filter types is a shape
 * neither route accepts. Keeping them apart is what lets the body builders below
 * be typed against the generated request models at all.
 */
export type BulkScope<TFilters> =
  | { kind: 'ids'; ids: string[] }
  | { kind: 'matching'; filters: TFilters };

export type ContactBulkScope = BulkScope<ContactListFilterInput>;
export type OrganizationBulkScope = BulkScope<OrganizationListFilterInput>;

/**
 * The scope half of a bulk request body — exactly one arm, matching the server's
 * validator.
 *
 * One builder per entity, each annotated with the GENERATED request model, so a
 * rename on either side of the schema is a compile error here rather than a body
 * the server answers 422 to at runtime.
 */
const contactBulkBody = (scope: ContactBulkScope): BulkContactRequest =>
  scope.kind === 'matching'
    ? { match_all_filtered: true, filters: scope.filters }
    : { ids: scope.ids };

const organizationBulkBody = (scope: OrganizationBulkScope): BulkOrganizationRequest =>
  scope.kind === 'matching'
    ? { match_all_filtered: true, filters: scope.filters }
    : { ids: scope.ids };

// Organization API
/**
 * A bulk delete is partial — rows a live record still depends on are KEPT, and
 * named so the user knows which of their own selection survived. `blocked` is
 * capped server-side; `blocked_count` is the true total.
 */
export interface BulkDeleteResult {
  deleted: number;
  blocked_count: number;
  blocked: DeletionBlockerSample[];
}

export const organizationApi = {
  list: async (params?: ListOrganizationsParams): Promise<PaginatedResponse<Organization>> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations`, { params });
    return {
      data: response.data.data,
      pagination: {
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      },
    };
  },

  get: async (id: string): Promise<Organization> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/${id}`);
    return response.data.data;
  },

  create: async (data: OrganizationCreate): Promise<Organization> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations`, data);
    return response.data.data;
  },

  update: async (id: string, data: OrganizationUpdate): Promise<Organization> => {
    const response = await axiosInstance.put(`${BASE_URL}/organizations/${id}`, data);
    return response.data.data;
  },

  /**
   * Assert or retract ONE relationship value. An incremental pair, deliberately.
   *
   * This column has several owners (the deal lifecycle, lead capture, the bank
   * tagger) and is written one element at a time, so sending the whole array
   * through `update` is a silent lost update — it discards everything added since
   * the page loaded. Adding is never refused; a removal 409s
   * (`RELATIONSHIP_PINNED`) while a live record still evidences the value.
   *
   * Returns the COMMITTED set, which is authoritative over anything the caller
   * computed optimistically.
   */
  addRelationship: async (id: string, value: string): Promise<string[]> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/organizations/${id}/relationship/${value}`
    );
    return response.data.data.relationship;
  },

  removeRelationship: async (id: string, value: string): Promise<string[]> => {
    const response = await axiosInstance.delete(
      `${BASE_URL}/organizations/${id}/relationship/${value}`
    );
    return response.data.data.relationship;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/organizations/${id}`);
  },

  archive: async (id: string): Promise<Organization> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/${id}/archive`);
    return response.data.data;
  },

  unarchive: async (id: string): Promise<Organization> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/${id}/unarchive`);
    return response.data.data;
  },

  bulkDelete: async (scope: OrganizationBulkScope): Promise<BulkDeleteResult> =>
    (await bulkDeleteOrganizationsApiV1MasterDataContactsOrganizationsBulkDeletePost(
      organizationBulkBody(scope),
    )) as BulkDeleteResult,

  deletionImpact: async (id: string): Promise<DeletionImpact> =>
    getOrganizationDeletionImpactApiV1MasterDataContactsOrganizationsOrganizationIdDeletionImpactGet(
      id,
    ),

  bulkArchive: async (scope: OrganizationBulkScope): Promise<{ archived: number }> =>
    (await bulkArchiveOrganizationsApiV1MasterDataContactsOrganizationsBulkArchivePost(
      organizationBulkBody(scope),
    )) as { archived: number },

  getIndustries: async (): Promise<string[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/industries`);
    return response.data.data;
  },

  enrichFromDomain: async (domain: string): Promise<DomainEnrichmentResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/enrich-domain`, { domain });
    return response.data.data;
  },

  lookupCompany: async (query: string): Promise<CompanyLookupResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/company-lookup`, { query });
    return response.data.data;
  },

  matchByDomain: async (domain: string): Promise<Organization | null> => {
    const response = await axiosInstance.get(`${BASE_URL}/organizations/match-domain`, {
      params: { domain },
    });
    return response.data.data;
  },

  checkDomain: async (domain: string, excludeCompanyId?: string): Promise<{ available: boolean; company_name: string | null }> => {
    const response = await axiosInstance.post(`${BASE_URL}/organizations/check-domain`, {
      domain,
      exclude_company_id: excludeCompanyId,
    });
    return response.data.data;
  },

  cascadeRelationship: async (
    organizationId: string,
    contactRelationship: string[],
    contactIds?: string[],
  ): Promise<{ updated_count: number }> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/organizations/${organizationId}/cascade-relationship`,
      {
        contact_relationship: contactRelationship,
        contact_ids: contactIds,
      },
    );
    return response.data.data;
  },
};

// Contact API (uses /people endpoints on backend)
export const contactApi = {
  list: async (params?: ListContactsParams): Promise<PaginatedResponse<Contact>> => {
    const response = await axiosInstance.get(`${BASE_URL}/people`, { params });
    return {
      data: response.data.data,
      pagination: {
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      },
    };
  },

  get: async (id: string): Promise<Contact> => {
    const response = await axiosInstance.get(`${BASE_URL}/people/${id}`, {
      params: { include_company: true },
    });
    return response.data.data;
  },

  create: async (data: ContactCreate): Promise<Contact> => {
    const response = await axiosInstance.post(`${BASE_URL}/people`, data);
    return response.data.data;
  },

  update: async (id: string, data: ContactUpdate): Promise<Contact> => {
    const response = await axiosInstance.put(`${BASE_URL}/people/${id}`, data);
    return response.data.data;
  },

  /**
   * Assert or retract ONE relationship value. An incremental pair, deliberately.
   *
   * This column has several owners (the deal lifecycle, lead capture, the bank
   * tagger) and is written one element at a time, so sending the whole array
   * through `update` is a silent lost update — it discards everything added since
   * the page loaded. Adding is never refused; a removal 409s
   * (`RELATIONSHIP_PINNED`) while a live record still evidences the value.
   *
   * Returns the COMMITTED set, which is authoritative over anything the caller
   * computed optimistically.
   */
  addRelationship: async (id: string, value: string): Promise<string[]> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/people/${id}/relationship/${value}`
    );
    return response.data.data.relationship;
  },

  removeRelationship: async (id: string, value: string): Promise<string[]> => {
    const response = await axiosInstance.delete(
      `${BASE_URL}/people/${id}/relationship/${value}`
    );
    return response.data.data.relationship;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/people/${id}`);
  },

  archive: async (id: string): Promise<Contact> => {
    const response = await axiosInstance.post(`${BASE_URL}/people/${id}/archive`);
    return response.data.data;
  },

  unarchive: async (id: string): Promise<Contact> => {
    const response = await axiosInstance.post(`${BASE_URL}/people/${id}/unarchive`);
    return response.data.data;
  },

  bulkDelete: async (scope: ContactBulkScope): Promise<BulkDeleteResult> =>
    (await bulkDeleteContactsApiV1MasterDataContactsPeopleBulkDeletePost(
      contactBulkBody(scope),
    )) as BulkDeleteResult,

  deletionImpact: async (id: string): Promise<DeletionImpact> =>
    getContactDeletionImpactApiV1MasterDataContactsPeopleContactIdDeletionImpactGet(id),

  bulkArchive: async (scope: ContactBulkScope): Promise<{ archived: number }> =>
    (await bulkArchiveContactsApiV1MasterDataContactsPeopleBulkArchivePost(
      contactBulkBody(scope),
    )) as { archived: number },
};

// Tier API
export const tierApi = {
  list: async (): Promise<Tier[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/tiers`);
    return response.data.data;
  },

  get: async (id: string): Promise<Tier> => {
    const response = await axiosInstance.get(`${BASE_URL}/tiers/${id}`);
    return response.data.data;
  },

  create: async (data: TierCreate): Promise<Tier> => {
    const response = await axiosInstance.post(`${BASE_URL}/tiers`, data);
    return response.data.data;
  },

  update: async (id: string, data: TierUpdate): Promise<Tier> => {
    const response = await axiosInstance.put(`${BASE_URL}/tiers/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/tiers/${id}`);
  },

  initializeDefaults: async (): Promise<Tier[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/tiers/initialize-defaults`);
    return response.data.data;
  },

  listCompanies: async (tierId: string, params?: { search?: string; offset?: number; limit?: number }): Promise<PaginatedResponse<Organization>> => {
    const response = await axiosInstance.get(`${BASE_URL}/tiers/${tierId}/organizations`, { params });
    return {
      data: response.data.data,
      pagination: {
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset,
      },
    };
  },

  assignCompanies: async (tierId: string, organizationIds: string[]): Promise<{ data: Organization[]; updated_count: number }> => {
    const response = await axiosInstance.put(`${BASE_URL}/tiers/${tierId}/organizations`, { company_ids: organizationIds });
    return response.data;
  },

  removeCompany: async (tierId: string, organizationId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/tiers/${tierId}/organizations/${organizationId}`);
  },
};

// Backlinks API (related entities)

/**
 * Preflight duplicate detection, shared by the contact and organization forms.
 *
 * Read-only mirror of the tiered check the create/update routes run, so a form
 * can warn while the user is still typing instead of bouncing them off a 409 at
 * save time. Resolves to `null` when the record looks new.
 */
export const duplicateCheckApi = {
  check: async (payload: DuplicateCheckPayload): Promise<DuplicateMatch | null> => {
    // The endpoint returns the generated DuplicateCheckResponse envelope
    // ({ match }), not the module's usual { data } wrapper.
    const response = await axiosInstance.post(`${BASE_URL}/duplicate-check`, payload);
    return response.data.match ?? null;
  },
};
